import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Table, message } from "antd";
import { KeyRound, RefreshCw } from "lucide-react";
import { MonoCell, StatusPill, TableCard, anTableProps, fmtDate, tableEmpty } from "../../design-system/tableUi";
import { AN_COLORS } from "../../design-system/tokens";
import {
  credentialErrorMessage,
  listProxyCredentials,
  revokeProxyCredential,
} from "../../service/dbProxyCredentialsService";
import { isAdminUser } from "../../utils/helpers";
import { relativeTime } from "./dbAgentModel";

/**
 * "Install credentials": one per db.env download (label, client ID, created,
 * last used). The secret is never returned by the API and never shown.
 */
const InstallCredentials = ({ now }) => {
  const isAdmin = isAdminUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notification, contextHolder] = message.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listProxyCredentials());
    } catch (err) {
      setRows([]);
      setError(credentialErrorMessage(err, "Could not load install credentials."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmRevoke = (row) => {
    Modal.confirm({
      title: `Revoke the install credential "${row.label || row.clientId}"?`,
      content:
        "The database agent using this credential stops getting login decisions from AuthNull until it gets a new db.env. This cannot be undone.",
      okText: "Revoke",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await revokeProxyCredential(row.id);
          notification.success({ content: "Install credential revoked." });
          load();
        } catch (err) {
          notification.error({ content: credentialErrorMessage(err, "Could not revoke the credential.") });
        }
      },
    });
  };

  const columns = [
    {
      title: "Install",
      dataIndex: "label",
      key: "label",
      render: (text) => <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{text || "Unnamed install"}</span>,
    },
    {
      title: "Client ID",
      dataIndex: "clientId",
      key: "clientId",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => fmtDate(v),
    },
    {
      title: "Last used",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (v) => (v ? <span title={fmtDate(v)}>{relativeTime(v, now)}</span> : "Never"),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, r) =>
        r.revokedAt ? <StatusPill status="revoked" label="Revoked" /> : <StatusPill status="active" label="Active" />,
    },
    ...(isAdmin
      ? [
          {
            title: "",
            key: "actions",
            width: 110,
            align: "right",
            render: (_, r) =>
              r.revokedAt ? null : (
                <Button size="small" danger onClick={() => confirmRevoke(r)}>
                  Revoke
                </Button>
              ),
          },
        ]
      : []),
  ];

  return (
    <section style={{ marginTop: "1.5rem" }}>
      {contextHolder}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "1rem",
          marginBottom: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              margin: 0,
              fontSize: "1rem",
              fontWeight: 600,
              color: AN_COLORS.ink,
            }}
          >
            <KeyRound size={16} /> Install credentials
          </h2>
          <div style={{ marginTop: "0.25rem", fontSize: "0.85rem", color: AN_COLORS.muted }}>
            Each db.env download gets its own credential. Revoke one to cut off that install.
          </div>
        </div>
        <Button icon={<RefreshCw size={13} />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={rows}
          loading={loading}
          rowKey={(r) => r.id ?? r.clientId}
          locale={tableEmpty(loading, error || "No install credentials yet. Downloading a db.env issues one.")}
          pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        />
      </TableCard>
    </section>
  );
};

export default InstallCredentials;
